/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file Weirwood Error SDK 主类，基于 Sentry Javascript SDK 二次开发
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */

import {
    subscribe,
    unsubscribe,
    installGlobalHandler,
    installGlobalUnhandledRejectionHandler,
    computeStackTrace
} from './vender/tracekit';
import {md5} from './vender/md5';

import {
    isEmptyObject,
    isFunction,
    isErrorEvent,
    isDOMException,
    isError,
    isPlainObject,
    isObject,
    isUndefined,
    isString
} from './utils/is';
import {installResourceLoadErrorsHandler, ResourceError} from './utils/resource-monitor';
import {supportsFetch, supportsReferrerPolicy} from './utils/supports';
import {
    each,
    sanitize,
    truncate,
    multipleTest,
    evaluateHash,
    isSameException,
    serializeException,
    getExceptionString,
    getAllExceptionString,
    serializeKeysForMessage,
    computeAPIExceptionDataId,
    computeAPIExceptionIssueId
} from './utils/misc';
import {getGlobal} from './utils/global';
import {
    WeirwoodErrorOptions,
    WeirwoodErrorContext,
    StackInfo,
    StackFrame,
    Request,
    Behavior,
    Payload,
    CaptureExOpitons,
    APIException
} from './interface/options.interface';
import {prepareFrames, computeFrames, computeTransactionUrl} from './stackframe';
import {stringify} from './vender/stringify';
import {BehaviorPlugin, TryCatchPlugin, UserAgentPlugin} from './plugins';

// 在 declaration.d.ts 中声明没有用处，需要这里重新申明下，后续检查
declare var XDomainRequest: any;

/**
 * 默认属性
 */
const defaultOptions: WeirwoodErrorOptions = {
    collectWindowErrors: true,
    collectUnhandledRejections: true,
    collectResourceLoadErrors: false,
    collectAutoAPIException: true,
    sampleRate: 1,
    maxUrlLength: 250,
    maxMessageLength: 0,
    allowDuplicates: false,
    stackTraceLimit: 50,
    behaviorLimit: 10,
    plugins: [],
    ignoreErrors: [],
    ignoreKeys: [],
    whitelistUrls: [],
    apiWhitelistUrls: [],
    resourceWhitelistUrls: [],
    ignoreUrls: [],
    ignoreJsUrls: [],
    ignoreApiUrls: [],
    ignoreApiStatus: [],
    ignoreResourceUrls: [],
    maxMessagePerMinute: 10,
    endpoint: 'https://fclog.baidu.com/log/weirwood?type=error'
};

const CDN_ADDRESS = 'https://dmpstatic.cdn.bcebos.com/weirwood';

/**
 * 异常监控主类
 * @class ErrorClient
 */
export class ErrorClient {
    /** 全局属性 */
    options: WeirwoodErrorOptions = defaultOptions;

    /** 全局上下文环境数据 */
    _globalContext: WeirwoodErrorContext = {
        // 其它信息
        env: {},

        // 用户信息
        user: {},

        //  为了避免上报日志中的 buildid、event_id、data_id、sessionId 的命名不一致性，勉强命名为 session_id
        session_id: ''
    };

    // weirwood 默认发送地址，如果API中包含这个地址，说明是 weirwood 自身的请求，需要过滤
    _globalKey = 'fclog.baidu.com/log';

    /** 当前异常携带的临时环境数据，一个异常提交后会重置 */
    _currentExceptionContext: any = {};

    /** 一个异常的开始时间，一个异常提交后会重置该时间，当前时间减去该参数可以知道有多长时间没有异常产生 */
    _currentExceptionStartTime = Date.now();

    /** 一个异常发生时的前序行为数据 */
    _behaviors: Behavior[] = [];

    /** 保留最近一次异常数据，用于对比当前异常和前一次异常是否属于同一个异常 */
    _lastData: Payload | null = null;
    _lastAPIData: any = null;

    /** 上一分钟已发送的异常数量，如果超过某个阈值，此分钟不会再发送 */
    _messagesSendLastMinute = 0;

    /** 用于记录异常数目清空后的开始时间 */
    _messagesSendEmptyStartTime = Date.now();

    /** 是否已经安装在全局安装 */
    _installed = false;

    /** 全局错误处理函数 */
    _globalErrorHandler: Function | null = null;

    /** fetch 请求默认配置 */
    _fetchDefaults = {
        method: 'POST',
        credentials: 'include',
        // Despite all stars in the sky saying that Edge supports old draft syntax,
        // aka 'never', 'always', 'origin' and 'default
        // https://caniuse.com/#feat=referrer-policy
        // It doesn't. And it throw exception instead of ignoring this parameter...
        // REF: https://github.com/getsentry/raven-js/issues/1233
        referrerPolicy: supportsReferrerPolicy() ? 'origin' : ''
    };

    /** 记录修改过的原生方法数组，便于后续还原 */
    _wrappedBuiltIns: any[] = [];

    /** 浏览器请求信息 */
    _requestInfo: Request | null = null;

    /**
     * 构造函数，new ErrorClient() 时调用，如果传入参数包含 token 和 buildid，会直接初始化，否则需要手动调用 `init` 函数初始化
     * @constructor
     * @param options 选项，可选
     */
    constructor(options?: WeirwoodErrorOptions) {
        if (options) {
            this.config(options);
        }
    }

    /**
     * 初始化，进行配置和安装，如果已经通过构造函数安装，这里不会重复安装 TODO: 这个方法还有没有必要存在
     * @param options 选项
     */
    init(options: WeirwoodErrorOptions) {
        return this.config(options);
    }

    /**
     * 配置选项，主要包括过滤器等，之后进行安装
     * @param options 选项
     * @return 实例
     */
    config(options: WeirwoodErrorOptions) {
        this.options = {...this.options, ...options};
        this.options.sendWindow = this.options.sendWindow || getGlobal();

        this._globalContext.session_id = this.options.uuid4();

        // 如果没有配置数据源，直接退出
        const {token, buildid} = this.options;
        if (!token || !buildid) {
            throw new Error('error: token and buildid must be provided');
        }

        // 设置一些过滤器
        this.setFilters();

        // 安装错误处理器
        this.install();

        // 链式返回
        return this;
    }

    /**
     * 根据选项，安装相应钩子，进行具体错误监听
     */
    install() {
        // 避免重复安装
        if (this._installed) {
            console.error('error: Weirwood Error SDK has been installed and configured');
            return false;
        }

        // 监听 window.onerror
        // 设置 window.onerror = traceKitWindowOnError， 会调用 notifyHandlers 函数，依次调用注册的 handler
        // 因此我们需要做的就是通过 subscribe 添加 handler 即可
        if (this.options.collectWindowErrors) {
            installGlobalHandler();
        }

        // 每分钟消息数量限制在 10 ~ 60 个，默认为 10 个，此属性暂时不暴露
        if (this.options.maxMessagePerMinute >= 60) {
            this.options.maxMessagePerMinute = 60;
        }

        // 监听 window.onunhandledrejection
        if (this.options.collectUnhandledRejections) {
            installGlobalUnhandledRejectionHandler();
        }

        // 获取错误处理器，之后在卸载时用到
        this._globalErrorHandler = this._getErrorHandler().bind(this);

        // 添加 handler 进行错误处理，否则即使调用了 `installGlobalHandler` 等，没有 handler 也没有用
        subscribe(this._globalErrorHandler);

        // 安装资源加载错误监控异常
        if (this.options.collectResourceLoadErrors) {
            installResourceLoadErrorsHandler((resourceError: ResourceError) => {
                // 记录当时资源加载异常发生错误时的 timing，有些浏览器目前不支持
                // see https://github.com/w3c/resource-timing/issues/12
                let timing: any;
                if (window.performance && window.performance.getEntriesByName) {
                    const entries = window.performance.getEntriesByName(resourceError.asset);
                    if (entries.length > 0) {
                        timing = entries[0];

                        ['name', 'serverTiming'].forEach(field => {
                            delete timing[field];
                        });
                    }
                }

                const stackInfo: StackInfo = {
                    mechanism: resourceError.type, // 通过 mechanism 获取资源错误类型，无需根据 url 再去判断
                    name: 'ResourceError',
                    message: resourceError.asset,
                    stack: [],
                    mode: 'onerror',
                    timing,
                    page: resourceError.page,
                    element: resourceError.element,
                    elementPath: resourceError.path.slice(0, 255) // 最多保存 255 个字符
                };
                this._handleStackInfo(stackInfo, {
                    trimHeadFrames: 0,
                    mechanism: stackInfo.mechanism || 'generic',
                    includePaths: this.options.includePaths
                });
            });
        }

        // 安装标志位
        this._installed = true;

        // 安装各个插件
        this.installPlugins();

        // 链式返回
        return this;
    }

    /** 安装各个插件 */
    installPlugins() {
        // 三个默认插件
        this.options.plugins = [
            new BehaviorPlugin(this.options.behaviorOptions),
            new TryCatchPlugin(this.options.tryCatchOptions),
            new UserAgentPlugin(),
            ...(this.options.plugins || [])
        ];

        for (const p of this.options.plugins) {
            p.setupOnce(this);
        }
    }

    /**
     * 设置一些过滤器，哪些前端错误需要忽略，例如 ingoreErrors 针对 message 来过滤
     */
    setFilters() {
        const options = this.options;

        // 忽略错误，可以设置字符串和正则表达式，内置两条基本规则，忽略跨域脚本异常 Script error
        if (options.ignoreErrors) {
            options.ignoreErrors = [
                /^Script error\.?$/,
                /^Javascript error: Script error\.? on line 0$/,
                '{"isTrusted":true}',
                '{"isTrusted":false}',
                'Network Error',
                'ResizeObserver loop',
                ...(options.ignoreErrors || [])
            ];
        }

        // 默认不上报CDN引用时脚本自身的异常，通过 NPM 引入时，在堆栈帧已经过滤
        options.ignoreJsUrls = [
            'chrome-extension',
            'safari-extension',
            'moz-extension',
            CDN_ADDRESS,
            ...(options.ignoreJsUrls || [])
        ];
    }

    /**
     * 获取异常监控处理函数
     * @returns Function
     */
    _getErrorHandler() {
        /**
         * @param stackInfo 堆栈
         * @param isWindowError 是否为顶层 window onerror 错误
         * @param error 处理的错误对象(如果可以获取)
         */
        return (stackInfo: StackInfo, isWindowError: boolean, error: any, url?: any, lineNo?: any, columnNo?: any) => {
            // 获取原始堆栈信息，避免处理堆栈方法存在问题，辅助排查
            if (error && (error.stack || error.stacktrace)) {
                stackInfo.originalStack = ((error.stack || error.stacktrace) + '').slice(0, 10000);
            }

            // 通常发生在 SyntaxError 的场景，此场景下无法获取到触发脚本
            // 所以无堆栈信息但是可以获取到 url 时将 url、line、column 填充至堆栈信息
            if (stackInfo.stack && !stackInfo.stack.length && url) {
                stackInfo.stack[0] = {
                    url: url,
                    line: lineNo,
                    column: columnNo
                };
            }

            // 如果是 promise 异常，没有 name 和 message 信息，针对 reject 的内容进行处理，SDK 层面不进行处理
            if (!stackInfo.name && !stackInfo.message && stackInfo.mechanism === 'onunhandledrejection') {
                const _window = getGlobal() as any;
                stackInfo.name = 'unhandledrejection';
                stackInfo.url = stackInfo.url || _window.location.href;

                // 这里不进行处理，因此 message 不一定是字符串，可能是任何对象
                stackInfo.message = error;
            }

            this._handleStackInfo(stackInfo, {
                trimHeadFrames: 0,
                mechanism: stackInfo.mechanism || 'generic',
                includePaths: this.options.includePaths
            });
        };
    }

    /**
     * 处理通过 tracekit 收集到的异常信息
     * @param stackInfo tracekit 收集到并且格式化后的异常信息
     * @param options 额外选项，包括错误来源？ TODO: 是否包括其它的，否则这个可以干掉，毕竟 stackInfo 包含了错误来源
     */
    _handleStackInfo(stackInfo: StackInfo, options: CaptureExOpitons) {
        const normalizedStackFrames = prepareFrames(stackInfo, options, this.options.stackTraceLimit);
        this._processException(stackInfo, normalizedStackFrames, options);
    }

    /**
     * 处理异常数据，并发送处理好的异常数据
     * @param stackInfo 堆栈异常
     * @param frames 规范化异常堆栈列表
     * @param options 选项
     */
    _processException(stackInfo: StackInfo, frames: StackFrame[], options: CaptureExOpitons) {
        const globalOptions = this.options;
        const {
            ignoreErrors = [],
            ignoreKeys = [],
            whitelistUrls = [],
            resourceWhitelistUrls = [],
            ignoreUrls = [],
            ignoreJsUrls = [],
            ignoreResourceUrls = []
        } = globalOptions;
        const {
            name: type,
            message,
            url: fileurl = '',
            page = window.location.href,
            lineno = 0,
            originalStack = ''
        } = stackInfo;

        // 错误 message 过滤
        if (ignoreErrors.length && multipleTest(ignoreErrors, message)) {
            return;
        }
        // 页面URL过滤
        if (ignoreUrls.length && multipleTest(ignoreUrls, page)) {
            return;
        }

        // 计算错误来源 URL
        const url = computeTransactionUrl(frames, fileurl);

        // 针对资源加载异常，进行黑白名单过滤
        if (type === 'ResourceError') {
            // 黑名单过滤
            if (ignoreResourceUrls.length && multipleTest(ignoreResourceUrls, message)) {
                return;
            }
            // 白名单过滤
            if (resourceWhitelistUrls.length && !multipleTest(resourceWhitelistUrls, message)) {
                return;
            }
        } else {
            // 针对 JS 异常，进行黑白名单过滤
            // JS错误类型过滤
            if (ignoreKeys.length && multipleTest(ignoreKeys, type)) {
                return;
            }
            // 黑名单过滤
            if (ignoreJsUrls.length && multipleTest(ignoreJsUrls, url)) {
                return;
            }
            // 白名单过滤
            if (whitelistUrls.length && !multipleTest(whitelistUrls, url)) {
                return;
            }
        }

        // 自身脚本异常过滤
        if (frames && frames.length > 0) {
            const filename = frames[0].filename || '';
            const isFromNpm = filename.indexOf('@baidu/weirwood-error') !== -1;
            const isFromCdn = filename.indexOf(CDN_ADDRESS) !== -1;
            if (isFromNpm || isFromCdn) {
                return;
            }
        }

        // 如果是自身异常，过滤掉
        if (url.indexOf(CDN_ADDRESS) !== -1) {
            return;
        }

        // 处理堆栈，该倒序倒序，没有堆栈构造一个堆栈
        const finalFrames = computeFrames(frames, fileurl, lineno);

        // 构造数据
        const data: any = {
            ...options,
            exception: {
                type: type,
                value: message,
                originalStack: originalStack,
                stacktrace: finalFrames,
                url: type === 'ResourceError' ? message : url,
                page: stackInfo.page,
                element: stackInfo.element,
                timing: stackInfo.timing,
                elementPath: stackInfo.elementPath
            }
        };

        // 获取第一个异常，如果没有类型和值，构造一个无法恢复的异常
        const ex = data.exception;
        if (ex.type == null && ex.value === '') {
            ex.value = 'Unrecoverable error caught';
        }

        // 将外层的 mechanism 移到 exception 里层
        if (!data.exception.mechanism && data.mechanism) {
            data.exception.mechanism = data.mechanism;
            delete data.mechanism;
        }

        // 发送数据
        this._send(data);
    }

    /**
     * 发送前 merge context 信息
     * @param data 待发送数据
     */
    mergeGlobalContext(data) {
        const _window = getGlobal() as any;

        // 合并额外数据
        data.env = {
            ...this._globalContext.env,
            ...data.env,
            network: _window.navigator.connection?.effectiveType,
            windowHeight: (_window.document.documentElement || _window.document.body).clientHeight,
            windowWidth: (_window.document.documentElement || _window.document.body).clientWidth,
            documentTitle: _window.document.title
        };

        // 如果有临时设置的，优先级比较高，覆盖持久化环境信息
        for (const k of Object.keys(this._currentExceptionContext)) {
            data.env[k] = this._currentExceptionContext[k];
        }

        // 记录异常发生到发送时间长度
        data.env['session:duration'] = Date.now() - this._currentExceptionStartTime;

        // 合并当前用户信息
        if (this._globalContext.user) {
            data.user = this._globalContext.user;
        }

        // 合并当前 session 信息
        if (this._globalContext.session_id) {
            data.session_id = this._globalContext.session_id;
        }
    }

    /**
     * 收集额外数据，数据预处理，并发送数据
     * @param data 组装好的异常数据
     */
    _send(data: any) {
        const baseData: any = {
            request: this._requestInfo
        };

        // 删除额外字段
        if (!isUndefined(data.trimHeadFrames)) {
            delete data.trimHeadFrames;
        }

        // 数据汇总
        data = {...baseData, ...data};

        this.mergeGlobalContext(data);

        // 合并行为信息数据
        if (this._behaviors && this._behaviors.length > 0) {
            // intentionally make shallow copy so that additions
            // to breadcrumbs aren't accidentally sent in this request
            let values = [].slice.call(this._behaviors, 0);
            if (this.options.behaviorDataCallback) {
                values = this.options.behaviorDataCallback(values);
            }
            data.behaviors = values;
        }

        const globalOptions = this.options;

        // 项目 token
        if (globalOptions.token) {
            data.token = globalOptions.token;
        }

        // 项目 buildid
        if (globalOptions.buildid) {
            data.buildid = globalOptions.buildid;
        }

        // 敏感数据过滤，例如密码，其余一些敏感信息
        data = this._sanitizeData(data);

        // 清除空对象
        Object.keys(data).forEach(function (key) {
            if (data[key] == null || data[key] === '' || isEmptyObject(data[key])) {
                delete data[key];
            }
        });

        // 发送数据前，还可以进行检测一波，是否需要发送数据，不需要发送的数据会返回 falsey 值
        if (isFunction(globalOptions.dataCallback)) {
            data = globalOptions.dataCallback(data);
        }

        // 如果没有数据了，那还发送啥？直接取消发送吧
        if (!data || isEmptyObject(data)) {
            return;
        }

        // 如果当前发送的数量已经超过了每分钟发送的阈值，取消发送
        // 下一分钟开始，需要重置 _messagesSendLastMinute 为 0，以及开始时间
        const duration = Date.now() - this._messagesSendEmptyStartTime;
        const lessThanAMinute = duration < 60000;
        const overflow = this._messagesSendLastMinute > this.options.maxMessagePerMinute;
        if (lessThanAMinute) {
            if (overflow) {
                return;
            }
        } else {
            this._messagesSendEmptyStartTime = Date.now();
            this._messagesSendLastMinute = 1;
        }

        // 如果设置了抽样频率，进行过滤
        if (typeof globalOptions.sampleRate === 'number') {
            if (Math.random() < globalOptions.sampleRate) {
                this._sendProcessedPayload(data);
                this._messagesSendLastMinute += 1;
            }
        } else {
            this._sendProcessedPayload(data);
            this._messagesSendLastMinute += 1;
        }
    }

    /**
     * 是否安装
     */
    isSetup() {
        return this._installed;
    }

    /**
     * 修剪过长请求
     * @param d
     */
    _trimPacket(d: Payload) {
        const maxUrlLength = this.options.maxUrlLength || 0;
        const maxMessageLength = this.options.maxMessageLength || 0;

        // 修剪错误消息
        if (d.exception) {
            d.exception.value = truncate(d.exception.value, maxMessageLength);
        }

        // 修剪 URL
        if (d.request) {
            const _window = getGlobal() as any;

            // 由于初始化后，这个地址 url 固定了，这里需要更改为最新的 url
            // 这里看参数是否需要模糊化
            d.request.url = truncate(_window.location.href, maxUrlLength);
            const referrerUrl = d.request.headers.Referrer;
            d.request.headers.Referrer = truncate(referrerUrl, maxUrlLength);
        }

        // 修剪行为数据
        if (d.behaviors && d.behaviors.length) {
            const urlProps = ['url', 'to', 'from'];
            for (const b of d.behaviors) {
                const dataObj = b.data || {};
                for (const k in dataObj) {
                    if (dataObj.hasOwnProperty(k)) {
                        urlProps.forEach(url => {
                            if (k === url) {
                                dataObj[k] = truncate(dataObj[k], maxUrlLength);
                            }
                        });
                    }
                }
            }
        }

        return d;
    }

    /**
     * 实际发送请求，该方法与 fetch 或 xhr 打交道
     * @param data 最终处理好的数据
     * @param callback 成功或者失败后的回调函数
     */
    _sendProcessedPayload(data: Payload, callback?: Function) {
        const self = this;
        const globalOptions = this.options;

        // 清除过长的数据
        data = this._trimPacket(data);

        // 如果不允许发送重复异常，取消发送
        if (!this.options.allowDuplicates && this._isRepeatData(data)) {
            console.warn('Weirwood error SDK dropped repeat event:', data);
            return;
        }

        // 对于资源和js异常进行issueGroupby操作
        let fingerprint;
        const issueGroupby = globalOptions.issueGroupby;
        if (issueGroupby && isFunction(issueGroupby)) {
            fingerprint = issueGroupby(data.exception);
        }

        // 异常基本信息标识
        if (fingerprint && isString(fingerprint)) {
            data.fingerprint = fingerprint.substr(0, 255);
            data.event_id = md5(data.fingerprint);
        } else {
            data.event_id = md5(getExceptionString(data));
        }

        // 异常标识，包括基本信息和环境信息
        data.data_id = md5(getAllExceptionString(data));

        // SDK 版本
        data.version = this.options.version || '';

        // 时间戳
        data.sdkTimestamp = Date.now();

        // 设置 lastData
        this._lastData = data;

        // 设置认证参数
        const auth: any = {
            weirwood_version: this.options.version || ''
        };

        // 开始实际调用
        (globalOptions.transport || this._makeRequest).call(this, {
            url: this.options.endpoint,
            auth: auth,
            data: data,
            options: globalOptions,
            onSuccess: function success() {
                self._resetBackoff();
                callback && callback();
            },
            onError: function failure(error: any) {
                if (error.request) {
                    self._setBackoffState(error.request);
                }
                error = error || new Error('Raven send failed (no additional details provided)');
                callback && callback(error);
            }
        });
    }

    /**
     * 发送实际请求，优先使用 fetch、之后使用 XMLHttpRequest，如果都不支持，使用 XDomainRequest（IE7\8）
     * @param opts 参数
     */
    _makeRequest(opts: any) {
        // Auth 通过 query string 发送，而不是 custom HTTP header，这样可以避免 preflight CORS 请求
        const self = this;
        let url = opts.url;
        let evaluatedHeaders: any = null;
        let evaluatedFetchParameters: any = {};

        // 计算请求头，通过 options 传入
        if (opts.options.headers) {
            evaluatedHeaders = evaluateHash(opts.options.headers);
        }

        // 计算 fetch 参数
        if (opts.options.fetchParameters) {
            evaluatedFetchParameters = evaluateHash(opts.options.fetchParameters);
        }
        const sendWindow = this.options.sendWindow;
        if (supportsFetch()) {
            evaluatedFetchParameters.body = stringify(opts.data);

            const fetchOptions = {...this._fetchDefaults, ...evaluatedFetchParameters};

            if (evaluatedHeaders) {
                const myHeaders = new Headers();
                for (const key of Object.keys(evaluatedHeaders)) {
                    myHeaders.append(key, evaluatedHeaders[key]);
                }

                myHeaders.append('Content-Type', 'text/plain');
                fetchOptions.headers = myHeaders;
            }
            return sendWindow
                .fetch(url, fetchOptions)
                .then(function (response: any) {
                    if (response.ok) {
                        opts.onSuccess && opts.onSuccess();
                        self._resetBackoff();
                    } else {
                        const error: any = new Error('Weirwood error SDK error code: ' + response.status);
                        // It's called request only to keep compatibility with XHR interface
                        // and not add more redundant checks in setBackoffState method
                        error.request = response;
                        opts.onError && opts.onError(error);
                    }
                })
                ['catch'](function () {
                    opts.onError && opts.onError(new Error('Weirwood error SDK error code: network unavailable'));
                    self._resetBackoff();
                });
        }

        let request = sendWindow.XMLHttpRequest && new sendWindow.XMLHttpRequest();
        if (!request) {
            return;
        }

        // 如果浏览器不支持 CORS(例如 IE7)，退出
        const hasCORS = 'withCredentials' in request || typeof XDomainRequest !== 'undefined';

        if (!hasCORS) {
            return;
        }

        if ('withCredentials' in request) {
            request.onreadystatechange = function () {
                if (request.readyState !== 4) {
                    self._resetBackoff();
                    return;
                } else if (request.status === 200) {
                    opts.onSuccess && opts.onSuccess();
                } else if (opts.onError) {
                    self._resetBackoff();
                    const err: any = new Error('Weirwood error SDK error code: ' + request.status);
                    err.request = request;
                    opts.onError(err);
                }
            };
        } else {
            // XDomainRequest is an implementation of HTTP access control (CORS) that worked
            // in Internet Explorer 8 and 9. It was removed in Internet Explorer 10 in favor of
            // using XMLHttpRequest with proper CORS; if you are targeting Internet Explorer 10
            // or later, or wish to support any other browser, you need to use standard HTTP access control.
            request = new XDomainRequest();
            // xdomainrequest cannot go http -> https (or vice versa),
            // so always use protocol relative
            url = url.replace(/^https?:/, '');

            // onreadystatechange not supported by XDomainRequest
            if (opts.onSuccess) {
                request.onload = opts.onSuccess;
            }
            if (opts.onError) {
                request.onerror = function () {
                    const err: any = new Error('Weirwood error SDK error code: XDomainRequest');
                    err.request = request;
                    opts.onError(err);
                };
            }
        }

        request.open('POST', url);

        if (evaluatedHeaders) {
            each(evaluatedHeaders, function (key: any, value: any) {
                request.setRequestHeader(key, value);
            });
        }

        request.send(stringify(opts.data));
    }

    /**
     * 敏感数据过滤
     * @param d 原始数据
     */
    _sanitizeData(d: Payload) {
        const sanitizeKeys = this.options.sanitizeKeys || [];
        if (sanitizeKeys.length) {
            return sanitize(d, sanitizeKeys);
        }
        return d;
    }

    /**
     * 捕获浏览器信息
     * @public
     * @param requestInfo 浏览器信息
     */
    captureUserAgent(requestInfo: Request) {
        this._requestInfo = requestInfo;
    }

    /**
     * 手动捕获异常
     * @public
     * @param ex 异常对象，可以是 Error 或者 ErrorEvent，或者普通对象
     * @param options 异常选项
     */
    captureException(ex: any, options?: any) {
        if (!options) {
            options = {trimHeadFrames: 0, includePaths: [], mechanism: 'generic', message: ''};
        }

        if (isError(ex)) {
            ex = ex;
        } else if (isErrorEvent(ex)) {
            ex = ex.error;
        } else if (isPlainObject(ex)) {
            options = this._getCaptureExceptionOptionsFromPlainObject(options, ex);
            ex = new Error(options.message);
        } else if (isDOMException(ex)) {
            const name = ex.name || 'DOMException';
            const message = ex.message ? name + ': ' + ex.message : name;
            return this.captureMessage(message, options);
        } else {
            return this.captureMessage(ex, {
                ...options,
                ...{
                    // if we fall back to captureMessage, default to attempting a new trace
                    stacktrace: true,
                    trimHeadFrames: options.trimHeadFrames + 1
                }
            });
        }

        try {
            // 可以传入自定义属性来覆盖默认属性
            options = {
                trimHeadFrames: 0,
                mechanism: 'generic',
                includePaths: this.options.includePaths || [],
                ...options
            };

            const stack: any = computeStackTrace(ex);
            this._handleStackInfo(stack, options);
        } catch (ex1) {
            // TraceKit.report will re-raise any exception passed to it,
            // which means you have to wrap it in try/catch. Instead, we
            // can wrap it here and only re-raise if TraceKit.report
            // raises an exception different from the one we asked to
            // report on.
            if (ex !== ex1) {
                throw ex1;
            }
        }

        return this;
    }

    /**
     * 判断是否为重复事件，即当前事件与上一次事件是一样的
     * @param data
     */
    _isRepeatData(data: Payload) {
        const last = this._lastData;
        const e1 = data.exception;
        const e2: any = last ? last.exception : {};
        return isSameException(e1, e2);
    }

    /**
     * 重置一些临时参数
     */
    _resetBackoff() {
        this._currentExceptionStartTime = Date.now();
        this._currentExceptionContext = {};
    }

    _setBackoffState(e: any) {
        console.log(e);

        // 重置一些状态
    }

    /**
     * 捕获自定义消息
     * @public
     * @param msg 自定义消息
     * @param options 额外参数
     * @param type 自定义异常的类型
     */
    captureMessage(msg: string, options: any = {}) {
        const globalOptions = this.options;
        const ignoreErrors = globalOptions.ignoreErrors || [];
        const ignoreKeys = globalOptions.ignoreKeys || [];
        const whitelistUrls = globalOptions.whitelistUrls || [];
        const ignoreUrls = globalOptions.ignoreUrls || [];
        const ignoreJsUrls = globalOptions.ignoreJsUrls || [];
        if (ignoreErrors.length && multipleTest(ignoreErrors, msg)) {
            return;
        }

        let ex: any;
        try {
            throw new Error(msg);
        } catch (ex1) {
            ex = ex1;
        }

        // ex.name = null;
        const stack: any = computeStackTrace(ex);
        let initialCall = Array.isArray(stack.stack) && stack.stack[1];

        // 计算 fileurl
        if (initialCall?.func?.indexOf('captureException') !== -1) {
            initialCall = stack.stack[2];
        }
        const fileurl = (initialCall && initialCall.url) || '';

        // 页面黑名单过滤
        if (ignoreUrls.length && multipleTest(ignoreUrls, window.location.href)) {
            return;
        }

        // JS错误类型过滤
        if (ignoreKeys.length && multipleTest(ignoreKeys, ex.name)) {
            return;
        }

        // js黑名单过滤
        if (ignoreJsUrls.length && multipleTest(ignoreJsUrls, fileurl)) {
            return;
        }

        // 白名单过滤
        if (whitelistUrls.length && !multipleTest(whitelistUrls, fileurl)) {
            return;
        }

        options = {...options, trimHeadFrames: 0, includePaths: globalOptions.includePaths};
        options.trimHeadFrames += 1;

        const frames = prepareFrames(stack, options);
        const newdata = {
            exception: {
                mechanism: 'manual',
                type: ex.name,
                value: ex.message,

                // 第一条记录是这个函数，因此需要去掉
                stacktrace: frames.slice(1).reverse(),
                url: fileurl
            }
        };

        this._send(newdata);
        return this;
    }

    /**
     * 捕获用户自行发送的 API 请求异常
     * @param api 用户自行封装的 API 请求异常
     */
    captureAPIException(api: APIException) {
        const global = getGlobal() as any;
        const {
            ignoreUrls = [],
            ignoreApiUrls = [],
            ignoreApiStatus = [],
            buildid,
            token,
            version,
            apiWhitelistUrls
        } = this.options;

        // 必填 url 和 method 以及 status，status 有可能为 0
        if (!(api.url && api.method && !isUndefined(api.status))) {
            console.error('API异常参数至少需要包含 method、url、status 参数。');
            return;
        }

        // 页面URL过滤
        if (ignoreUrls.length && multipleTest(ignoreUrls, global.location.href)) {
            return;
        }

        // 黑名单过滤，主要过滤 url
        if (ignoreApiUrls.length && multipleTest(ignoreApiUrls, api.url)) {
            return;
        }

        // 白名单过滤，过滤 api.url 参数
        if (apiWhitelistUrls.length && !multipleTest(apiWhitelistUrls, api.url)) {
            return;
        }
        // 黑名单过滤，过滤 api.status
        if (ignoreApiStatus.length && ignoreApiStatus.indexOf(Number(api.status)) !== -1) {
            return;
        }
        
        // 对api的url进行归一化模糊处理
        const exception = {...api, type: 'APIError'};
        // 附加请求头信息和项目信息
        let data: any = {
            request: {
                headers: {'User-Agent': global.navigator.userAgent},
                url: global.location.href
            },
            exception: exception,
            buildid: buildid,
            token: token,
            version: version || ''
        };

        const globalOptions = this.options;
        // 发送数据前，还可以进行检测一波，是否需要发送数据，不需要发送的数据会返回 falsey 值
        if (isFunction(globalOptions.dataCallback)) {
            data = globalOptions.dataCallback(data);
        }

        // 如果没有数据了，那还发送啥？直接取消发送吧
        if (!data || isEmptyObject(data)) {
            return;
        }

        this.mergeGlobalContext(data);

        // 对于资源和js异常进行issueGroupby操作
        let fingerprint;
        const issueGroupby = this.options.issueGroupby;
        if (issueGroupby && isFunction(issueGroupby)) {
            fingerprint = issueGroupby(data.exception);
        }

        // 计算 API issue id，这里是为了和原来的接口统一
        if (fingerprint && isString(fingerprint)) {
            data['fingerprint'] = fingerprint.substr(0, 255);
            data['event_id'] = md5(data['fingerprint']);
        } else {
            data['event_id'] = computeAPIExceptionIssueId(api);
        }

        // 计算整条日志 id
        data['data_id'] = computeAPIExceptionDataId(data);

        // 完全重复的API请求日志不进行发送
        if (this._lastAPIData === data['data_id']) {
            return;
        }
        this._lastAPIData = data['data_id'];
        const send = () => {
            (this.options.globalOptions?.transport || this._makeRequest).call(this, {
                url: this.options.endpoint,
                data: data,
                options: {},
                onSuccess() {},
                onError() {}
            });
        };
        // 如果设置了抽样频率，进行过滤
        if (typeof globalOptions.sampleRate === 'number') {
            if (Math.random() < globalOptions.sampleRate) {
                send();
            }
        } else {
            send();
        }
    }

    /** TODO */
    captureEvent() {}

    afterLoad() {
        const global = getGlobal();
        const RavenConfig = global['RavenConfig'];
        if (RavenConfig) {
            this.init(RavenConfig);
        }
    }

    _getCaptureExceptionOptionsFromPlainObject(currentOptions: any, ex: any) {
        const exKeys = Object.keys(ex).sort();
        const options = {
            ...currentOptions,
            ...{
                message: 'Non-Error exception captured with keys: ' + serializeKeysForMessage(exKeys),
                fingerprint: [md5(exKeys.join(''))],
                env: (currentOptions && currentOptions.env) || {}
            }
        };
        options.env.__serialized__ = serializeException(ex);

        return options;
    }

    /**
     * 主动捕获用户行为
     * @public
     * @param obj 行为数据
     */
    captureBehavior(obj: Behavior) {
        let crumb = {...obj, timestamp: Date.now() / 1000};

        if (isFunction(this.options.breadcrumbCallback)) {
            const result = this.options.breadcrumbCallback(crumb);
            if (isObject(result) && !isEmptyObject(result)) {
                crumb = result;
            } else if (result === false) {
                return this;
            }
        }

        this._behaviors.push(crumb);
        if (this._behaviors.length > (this.options.behaviorLimit || 10)) {
            this._behaviors.shift();
        }
        return this;
    }

    /**
     * 设置环境上下文数据，例如 userId, userName, userRole
     */
    setContext(args: {[k: string]: any}, options: {persist: boolean} = {persist: true}) {
        for (const k in args) {
            if (Reflect.has(args, k)) {
                if (options.persist) {
                    this._globalContext.env![k] = args[k];
                } else {
                    this._currentExceptionContext[k] = args[k];
                }
            }
        }
    }

    /**
     * 清空上下文环境数据
     */
    clearContext() {
        this._globalContext.env = {};
        this._currentExceptionContext = {};
    }

    /**
     * 恢复所有 hack 的内置函数
     */
    _restoreBuiltIns() {
        // restore any wrapped builtins
        let builtin;
        while (this._wrappedBuiltIns.length) {
            builtin = this._wrappedBuiltIns.shift();

            const obj = builtin[0];
            const name = builtin[1];
            const orig = builtin[2];

            obj[name] = orig;
        }
    }

    noConflict() {}

    /**
     * 卸载
     */
    uninstall() {
        this.dispose();
    }

    /**
     * 销毁当前 weirwood 实例，取消对 onerror 和 onunhandledrejection 的绑定
     */
    dispose() {
        unsubscribe(this._globalErrorHandler);

        this._restoreBuiltIns();
        this._globalErrorHandler = null;
        this._installed = false;
    }
}
